import { useEffect, useRef, useState } from "react";
import { ArrowDown, Download } from "lucide-react";

import { api } from "../lib/api.js";
import { Elapsed } from "./ui.jsx";

/**
 * The live deploy log.
 *
 * Server-sent events, resumed by byte offset, so a reconnect continues rather
 * than replaying a six-minute build. Autoscroll releases the moment you scroll up
 * — reading a failure while the log races away is the whole reason to look at it.
 */

const LEVEL_CLASS = {
    step: "text-blue-300 font-semibold mt-3 first:mt-0",
    ok: "text-green-300",
    fail: "text-red-300 font-medium",
    warn: "text-amber-300",
    err: "text-red-200/90",
    info: "text-gray-300",
    out: "text-gray-400"
};

/** `since` is the deploy's start; while the log is streaming the header counts from it. */
export function LogConsole({ deploymentId, status, since, className = "" }) {
    const [lines, setLines] = useState([]);
    const [connected, setConnected] = useState(false);
    const [done, setDone] = useState(null);
    const [follow, setFollow] = useState(true);
    const bottom = useRef(null);
    const box = useRef(null);

    useEffect(() => {
        if (!deploymentId) return undefined;
        setLines([]);
        setDone(null);

        const source = new EventSource(api.deployments.streamUrl(deploymentId));
        source.onopen = () => setConnected(true);
        source.addEventListener("line", (event) => {
            const entry = JSON.parse(event.data);
            setLines((previous) => [...previous, entry]);
        });
        source.addEventListener("done", (event) => {
            setDone(JSON.parse(event.data));
            setConnected(false);
            source.close();
        });
        source.onerror = () => setConnected(false);

        return () => source.close();
    }, [deploymentId]);

    useEffect(() => {
        if (follow) bottom.current?.scrollIntoView({ block: "end" });
    }, [lines, follow]);

    // Following is a consequence of being at the bottom, not a separate mode to manage.
    const onScroll = () => {
        const element = box.current;
        if (!element) return;
        setFollow(element.scrollHeight - element.scrollTop - element.clientHeight < 40);
    };

    return (
        <div className={`relative overflow-hidden rounded-lg bg-gray-900 ${className}`}>
            <div className="flex items-center justify-between border-b border-gray-800 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span className={`inline-block size-2 rounded-full ${connected ? "bg-blue-500 animate-pulse" : done?.status === "success" ? "bg-green-500" : done ? "bg-red-500" : "bg-gray-600"}`} />
                    {connected ? "streaming" : done ? done.status : status ?? "idle"}
                    {/* Only while it is actually running: a finished deploy streams too, for the moment it takes to replay. */}
                    {connected && status === "running" && since ? <span className="text-gray-500">· <Elapsed since={since} /></span> : null}
                    {done?.durationMs ? <span className="text-gray-500">· {(done.durationMs / 1000).toFixed(1)}s</span> : null}
                </div>
                <a
                    href={api.deployments.logUrl(deploymentId)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200"
                >
                    <Download size={12} /> raw
                </a>
            </div>

            <div
                ref={box}
                onScroll={onScroll}
                className="h-96 overflow-auto px-3 py-2 font-mono text-xs leading-relaxed"
            >
                {lines.length === 0 ? (
                    <p className="text-gray-600">waiting for output…</p>
                ) : (
                    lines.map((line, index) => (
                        <div key={index} className={`whitespace-pre-wrap break-all ${LEVEL_CLASS[line.level] ?? "text-gray-400"}`}>
                            {line.level === "step" ? `── ${line.text} ` : line.text}
                        </div>
                    ))
                )}
                {done?.reason ? <div className="mt-3 whitespace-pre-wrap text-red-300">{done.reason}</div> : null}
                <div ref={bottom} />
            </div>

            {!follow ? (
                <button
                    onClick={() => setFollow(true)}
                    className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-gray-800 px-3 py-1 text-xs text-gray-200 shadow hover:bg-gray-700"
                >
                    <ArrowDown size={12} /> follow
                </button>
            ) : null}
        </div>
    );
}
